/**
 * The last interview a user picked, persisted so a refresh does not drop them back on the
 * selector.
 *
 * This was a Redux slice behind redux-remember. Nothing outside InterviewsProvider ever read it —
 * every consumer went through `useInterviewOptions()` — so four dependencies and a store existed
 * to keep three strings in localStorage. Reading is synchronous here, which is why index.tsx no
 * longer has to hold render until a rehydrate finishes.
 */
const KEY = 'selectedInterview';

export interface SelectedInterviewInfo {
    id: string;
    name: string;
    groupId: string;
}

/** Returns undefined when nothing is stored, or when what is stored is not the shape we wrote. */
export function readSelectedInterview(): SelectedInterviewInfo | undefined {
    try {
        const raw = window.localStorage.getItem(KEY);
        if (!raw) return undefined;
        const parsed: unknown = JSON.parse(raw);
        if (!parsed || typeof parsed !== 'object') return undefined;
        const { id, name, groupId } = parsed as Partial<SelectedInterviewInfo>;
        if (typeof id !== 'string' || typeof groupId !== 'string' || !id || !groupId) {
            return undefined;
        }
        return { id, name: typeof name === 'string' ? name : '', groupId };
    } catch {
        // Private-mode Safari throws on localStorage, and a hand-edited value throws on parse.
        // Neither is worth failing a render over: the user just lands on the selector.
        return undefined;
    }
}

export function writeSelectedInterview(info: SelectedInterviewInfo): void {
    try {
        window.localStorage.setItem(KEY, JSON.stringify(info));
    } catch {
        // Selection still works for this session; it just will not survive a refresh.
    }
}

/**
 * Plain function rather than a hook so the class-component ErrorBoundary can call it. Needing
 * `connect` for exactly this is why that component was wired to Redux at all.
 */
export function clearSelectedInterview(): void {
    try {
        window.localStorage.removeItem(KEY);
    } catch {
        /* nothing to clear if storage is unavailable */
    }
}
